import { useEffect, useMemo, useState } from "react";
import {
	ShoppingCart,
	Trash2,
	ArrowLeft,
	Plus,
	Minus,
	Loader2,
	CreditCard,
	Truck,
	Home as HomeIcon,
	AlertCircle,
	Gift,
} from "lucide-react";
import { useCartStore } from "../store/cartStore";
import { supabase } from "../lib/supabase";
import { WEIGHT_BASED_METHOD, packageGrams, shippingCost as computeShippingCost, type ShippingMethod } from "../lib/shipping";
import { Link } from "react-router-dom";
import { useAuthStore } from "../store/authStore";
import { useNotification } from "../hooks/useNotification";
import { useOrdersEnabled } from "../hooks/useOrdersEnabled";
import { useShippingMethods } from "../hooks/useShippingMethods";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Alert, AlertDescription } from "../components/ui/alert";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../components/ui/select";
import { cn, formatEuro } from "../lib/utils";
import { productImage } from "../lib/productImage";

// Interfacce per gli indirizzi
interface Address {
	street: string;
	apartment?: string;
	city: string;
	postalCode: string;
	province: string;
	country: string;
}

interface ShippingInfo {
	customer_name: string;
	customer_email: string;
	customer_phone: string;
	shipping_address: Address;
	shipping_method: ShippingMethod;
	notes: string;
	gift_wrap: boolean;
}

const PROVINCES = ["TV", "VE", "PN", "UD", "PD", "Altra"];

export default function Cart() {
	const cartItems = useCartStore((state) => state.items);
	const removeItem = useCartStore((state) => state.removeItem);
	const updateQuantity = useCartStore((state) => state.updateQuantity);
	const [loading, setLoading] = useState(false);
	const { ordersEnabled, loading: ordersEnabledLoading } = useOrdersEnabled();
	const { methods: shippingMethods, tiers: shippingTiers, error: shippingError } = useShippingMethods();
	const [checkoutStep, setCheckoutStep] = useState<"cart" | "shipping">("cart");
	const [formError, setFormError] = useState<string | null>(null);
	const [promoCode, setPromoCode] = useState("");
	const { user } = useAuthStore();
	const { info: notifyInfo } = useNotification();

	const [shippingInfo, setShippingInfo] = useState<ShippingInfo>({
		customer_name: "",
		customer_email: user?.email || "",
		customer_phone: "",
		shipping_address: {
			street: "",
			apartment: "",
			city: "",
			postalCode: "",
			province: "",
			country: "Italia",
		},
		shipping_method: "standard",
		notes: "",
		gift_wrap: false,
	});

	const subtotal = cartItems.reduce((sum, item) => sum + item.price * item.quantity, 0);
	const totalItems = cartItems.reduce((sum, item) => sum + item.quantity, 0);

	// Prezzi dalle tabelle shipping_methods e shipping_weight_tiers (dashboard),
	// le stesse che usa create-checkout-session: quanto mostrato qui è quanto
	// viene addebitato.
	const activeShippingMethods = useMemo(() => shippingMethods.filter((m) => m.active), [shippingMethods]);
	const selectedShipping = activeShippingMethods.find((m) => m.value === shippingInfo.shipping_method);
	const parcelGrams = packageGrams(cartItems);
	const shippingCost = selectedShipping
		? computeShippingCost(selectedShipping, subtotal, parcelGrams, shippingTiers) ?? 0
		: 0;
	const standardShipping = activeShippingMethods.find((m) => m.value === "standard");
	const freeShippingFrom = standardShipping?.freeFrom ?? null;

	// Metodo scelto disattivato dalla dashboard: si passa al primo disponibile.
	useEffect(() => {
		if (activeShippingMethods.length > 0 && !selectedShipping) {
			setShippingInfo((prev) => ({ ...prev, shipping_method: activeShippingMethods[0].value }));
		}
	}, [activeShippingMethods, selectedShipping]);

	const handleQuantityChange = (variantId: string, newQuantity: number) => {
		if (newQuantity <= 0) {
			removeItem(variantId);
		} else {
			updateQuantity(variantId, newQuantity);
		}
	};

	const handleAddressChange = (field: keyof Address, value: string) => {
		setShippingInfo((prev) => ({
			...prev,
			shipping_address: { ...prev.shipping_address, [field]: value },
		}));
	};

	const total = subtotal + shippingCost;

	const createOrder = async (): Promise<string> => {
		const newOrderId = crypto.randomUUID();

		const addressPayload = {
			street: shippingInfo.shipping_address.street,
			apartment: shippingInfo.shipping_address.apartment || undefined,
			city: shippingInfo.shipping_address.city,
			postalCode: shippingInfo.shipping_address.postalCode,
			province: shippingInfo.shipping_address.province,
			country: shippingInfo.shipping_address.country,
		};

		const notes = [
			shippingInfo.gift_wrap ? "Confezione regalo richiesta." : null,
			shippingInfo.notes.trim() || null,
		]
			.filter(Boolean)
			.join(" ");

		const { error: orderError } = await supabase.from("orders").insert({
			id: newOrderId,
			user_id: user?.id ?? null,
			total_price: total,
			status: "pending",
			customer_email: shippingInfo.customer_email,
			customer_name: shippingInfo.customer_name,
			customer_phone: shippingInfo.customer_phone,
			shipping_address: addressPayload,
			billing_address: addressPayload,
			shipping_method: shippingInfo.shipping_method,
			shipping_price: shippingCost,
			payment_method: "stripe",
			payment_status: "pending",
			notes: notes || null,
		});

		if (orderError) {
			console.error("Order insert error:", orderError);
			throw new Error("Non è stato possibile creare l'ordine. Riprova tra qualche istante.");
		}

		return newOrderId;
	};

	const validateShippingForm = (): string | null => {
		if (!shippingInfo.customer_name || !shippingInfo.customer_email || !shippingInfo.customer_phone) {
			return "Completa i campi obbligatori: Nome e Cognome, Email, Telefono.";
		}
		if (
			!shippingInfo.shipping_address.street ||
			!shippingInfo.shipping_address.city ||
			!shippingInfo.shipping_address.postalCode ||
			!shippingInfo.shipping_address.province
		) {
			return "Completa i campi obbligatori dell'indirizzo di spedizione.";
		}
		return null;
	};

	const handleStripeCheckout = async () => {
		const validationError = validateShippingForm();
		if (validationError) {
			setFormError(validationError);
			return;
		}
		setFormError(null);

		try {
			setLoading(true);

			const newOrderId = await createOrder();

			for (const item of cartItems) {
				const { error: orderItemError } = await supabase.from("order_items").insert({
					order_id: newOrderId,
					product_id: item.productId,
					variant_id: item.id,
					variant_label: item.variantLabel,
					quantity: item.quantity,
					price: item.price,
				});

				if (orderItemError) {
					throw new Error("Errore nella creazione degli articoli ordine: " + orderItemError.message);
				}
			}

			// Solo formato e quantità: prezzo, nome e immagine finali li decide
			// l'edge function leggendo il database, non ciò che manda il client.
			const items = cartItems.map((item) => ({
				id: item.id,
				quantity: item.quantity,
			}));

			const { data, error } = await supabase.functions.invoke("create-checkout-session", {
				body: {
					items,
					customerEmail: shippingInfo.customer_email,
					customerName: shippingInfo.customer_name,
					customerPhone: shippingInfo.customer_phone,
					shippingAddress: shippingInfo.shipping_address,
					billingAddress: shippingInfo.shipping_address,
					shippingMethod: shippingInfo.shipping_method,
					orderId: newOrderId,
					successUrl: `${window.location.origin}/checkout-success?session_id={CHECKOUT_SESSION_ID}&order_id=${newOrderId}`,
					cancelUrl: `${window.location.origin}/checkout-success?cancelled=true&order_id=${newOrderId}`,
				},
			});

			if (error) {
				// Per una risposta non-2xx supabase-js dà solo un messaggio
				// generico in inglese: il motivo vero è nel corpo della risposta.
				let serverMessage = "";
				try {
					serverMessage = (await error.context?.json())?.error ?? "";
				} catch {
					// corpo non JSON: resta il messaggio generico sotto
				}
				throw new Error(serverMessage || "Non è stato possibile avviare il pagamento. Riprova tra qualche istante.");
			}
			if (!data?.url) throw new Error("Non è stato possibile avviare il pagamento. Riprova tra qualche istante.");

			// Il carrello resta: chi annulla su Stripe lo ritrova. Lo svuota CheckoutSuccess.
			window.location.href = data.url;
		} catch (error: any) {
			setFormError(error.message);
			console.error("Stripe checkout error:", error);
		} finally {
			setLoading(false);
		}
	};

	const handleApplyPromo = () => {
		notifyInfo("Il codice promozionale non è ancora disponibile.");
		setPromoCode("");
	};

	const OrderSummary = ({ ctaLabel, onCta, ctaIcon: CtaIcon }: {
		ctaLabel: string;
		onCta: () => void;
		ctaIcon: typeof ShoppingCart;
	}) => (
		<div className="rounded-2xl border border-border bg-card p-6 shadow-sm sticky top-8 space-y-5">
			<h2 className="font-display text-xl font-semibold">Riepilogo Ordine</h2>

			<div className="space-y-3">
				<div className="flex justify-between items-center text-sm border-b border-border pb-3">
					<span className="flex items-center gap-2 text-muted-foreground">
						Subtotale
						<Badge variant="secondary">{totalItems} articoli</Badge>
					</span>
					<span className="font-medium">{formatEuro(subtotal)}</span>
				</div>
				<div className="flex justify-between items-center text-sm border-b border-border pb-3">
					<span className="text-muted-foreground">Spedizione</span>
					{!selectedShipping ? (
						<span className="font-medium text-muted-foreground">—</span>
					) : shippingCost > 0 ? (
						<span className="font-medium">{formatEuro(shippingCost)}</span>
					) : (
						<span className="font-medium text-primary">Gratis</span>
					)}
				</div>
				<div className="flex justify-between items-center pt-1">
					<span className="font-semibold">Totale</span>
					<span className="font-bold text-lg text-primary">{formatEuro(total)}</span>
				</div>
			</div>

			{freeShippingFrom !== null && subtotal < freeShippingFrom && (
				<p className="text-xs text-muted-foreground">
					Mancano {formatEuro(freeShippingFrom - subtotal)} alla spedizione standard gratuita.
				</p>
			)}

			{shippingError && (
				<Alert variant="destructive">
					<AlertCircle className="h-4 w-4" />
					<AlertDescription>{shippingError}. Ricarica la pagina e riprova.</AlertDescription>
				</Alert>
			)}

			{!ordersEnabled && !ordersEnabledLoading && (
				<Alert>
					<AlertCircle className="h-4 w-4" />
					<AlertDescription>
						Gli ordini online non sono ancora attivi. Per acquistare puoi{" "}
						<Link to="/contact" className="underline hover:text-primary">
							contattarci
						</Link>
						.
					</AlertDescription>
				</Alert>
			)}

			<Button
				onClick={onCta}
				disabled={loading || !ordersEnabled || !selectedShipping}
				className="w-full"
				size="lg"
				shape="card"
			>
				{loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <CtaIcon className="h-5 w-5" />}
				{ctaLabel}
			</Button>

			<Link
				to="/products"
				className="block text-center text-sm text-muted-foreground hover:text-primary transition-colors"
			>
				Continua gli acquisti
			</Link>
		</div>
	);

	return (
		<section className="bg-muted/40 py-10">
			<div className="container">
				<div className="flex flex-col gap-2 mb-8">
					<span className="w-12 h-1 rounded-full bg-primary" />
					<h1 className="font-display text-3xl font-bold text-foreground">
						{checkoutStep === "cart" ? "Il Tuo Carrello" : "Dati di Spedizione"}
					</h1>
				</div>

				{checkoutStep === "shipping" && (
					<div className="flex items-center gap-3 mb-8 text-sm">
						<span className="flex items-center gap-2 text-primary font-medium">
							<span className="h-6 w-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs">1</span>
							Carrello
						</span>
						<span className="w-10 h-px bg-border" />
						<span className="flex items-center gap-2 text-primary font-medium">
							<span className="h-6 w-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs">2</span>
							Spedizione
						</span>
					</div>
				)}

				{cartItems.length === 0 ? (
					<div className="bg-card p-10 rounded-2xl border border-border shadow-sm text-center">
						<ShoppingCart className="h-14 w-14 mx-auto text-muted-foreground mb-4" />
						<p className="text-muted-foreground text-lg mb-6">Il tuo carrello è vuoto.</p>
						<Button asChild size="lg" shape="card">
							<Link to="/products">Continua lo shopping</Link>
						</Button>
					</div>
				) : (
					<div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
						<div className="lg:col-span-2 space-y-4">
							{checkoutStep === "cart" && (
								<>
									{cartItems.map((item) => (
										<div
											key={item.id}
											className="bg-card rounded-2xl border border-border shadow-sm p-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4"
										>
											<div className="flex items-center gap-4 min-w-0">
												<img
													src={productImage(item.image)}
													alt={item.name}
													className="w-16 h-16 object-cover rounded-lg border border-border flex-shrink-0"
												/>
												<div className="min-w-0">
													<p className="font-semibold text-foreground truncate">{item.name}</p>
													<p className="text-sm text-muted-foreground">{item.variantLabel}</p>
													<p className="text-primary font-bold">{formatEuro(item.price)}</p>
												</div>
											</div>
											<div className="flex items-center justify-between gap-3 flex-shrink-0 sm:justify-end">
												<div className="flex items-center gap-1 rounded-full border border-border px-1 py-1">
													<Button
														variant="ghost"
														size="icon"
														className="h-10 w-10 rounded-full sm:h-7 sm:w-7"
														onClick={() => handleQuantityChange(item.id, item.quantity - 1)}
														aria-label="Diminuisci quantità"
													>
														<Minus className="h-3.5 w-3.5" />
													</Button>
													<span className="min-w-6 text-center text-sm font-semibold">{item.quantity}</span>
													<Button
														variant="ghost"
														size="icon"
														className="h-10 w-10 rounded-full sm:h-7 sm:w-7"
														onClick={() => handleQuantityChange(item.id, item.quantity + 1)}
														disabled={item.quantity >= item.stock}
														aria-label="Aumenta quantità"
													>
														<Plus className="h-3.5 w-3.5" />
													</Button>
												</div>
												<span className="font-bold min-w-16 text-right hidden sm:block">
													{formatEuro((item.price * item.quantity))}
												</span>
												<Button
													variant="ghost"
													size="icon"
													className="h-10 w-10 rounded-full text-danger hover:bg-destructive/10 hover:text-danger sm:h-9 sm:w-9"
													onClick={() => removeItem(item.id)}
													aria-label="Rimuovi prodotto"
												>
													<Trash2 className="h-4 w-4" />
												</Button>
											</div>
										</div>
									))}

									<div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between pt-2">
										<Link
											to="/products"
											className="inline-flex items-center text-primary hover:text-primary/80 transition-colors font-medium text-sm"
										>
											<ArrowLeft className="h-4 w-4 mr-2" />
											Continua gli acquisti
										</Link>
										<div className="flex gap-2 w-full sm:w-auto">
											<Input
												placeholder="Codice promozionale"
												value={promoCode}
												onChange={(e) => setPromoCode(e.target.value)}
												className="sm:w-56"
											/>
											<Button type="button" variant="outline" onClick={handleApplyPromo}>
												Applica
											</Button>
										</div>
									</div>
								</>
							)}

							{checkoutStep === "shipping" && (
								<div className="bg-card rounded-2xl border border-border shadow-sm p-6 md:p-8 space-y-8">
									{formError && (
										<Alert variant="destructive">
											<AlertCircle className="h-4 w-4" />
											<AlertDescription>{formError}</AlertDescription>
										</Alert>
									)}

									{/* Sezione 1: Contatti */}
									<div className="space-y-4">
										<h2 className="font-display text-lg font-semibold flex items-center gap-2">
											<span className="h-6 w-6 rounded-full bg-primary/10 text-primary text-xs font-bold flex items-center justify-center">1</span>
											I Tuoi Contatti
										</h2>
										<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
											<div className="space-y-1.5 md:col-span-2">
												<Label htmlFor="nome">Nome e Cognome *</Label>
												<Input
													id="nome"
													placeholder="Anna De Piccoli"
													value={shippingInfo.customer_name}
													onChange={(e) =>
														setShippingInfo((prev) => ({ ...prev, customer_name: e.target.value }))
													}
													required
												/>
											</div>
											<div className="space-y-1.5">
												<Label htmlFor="email">Email *</Label>
												<Input
													id="email"
													type="email"
													placeholder="anna@esempio.it"
													value={shippingInfo.customer_email}
													onChange={(e) =>
														setShippingInfo((prev) => ({ ...prev, customer_email: e.target.value }))
													}
													required
												/>
											</div>
											<div className="space-y-1.5">
												<Label htmlFor="telefono">Telefono *</Label>
												<Input
													id="telefono"
													placeholder="+39 340 000 0000"
													value={shippingInfo.customer_phone}
													onChange={(e) =>
														setShippingInfo((prev) => ({ ...prev, customer_phone: e.target.value }))
													}
													required
												/>
											</div>
										</div>
									</div>

									{/* Sezione 2: Indirizzo */}
									<div className="space-y-4">
										<h2 className="font-display text-lg font-semibold flex items-center gap-2">
											<span className="h-6 w-6 rounded-full bg-primary/10 text-primary text-xs font-bold flex items-center justify-center">2</span>
											Indirizzo di Consegna
										</h2>
										<div className="grid grid-cols-1 md:grid-cols-3 gap-4">
											<div className="space-y-1.5 md:col-span-2">
												<Label htmlFor="indirizzo">Indirizzo *</Label>
												<Input
													id="indirizzo"
													placeholder="Via Roma 12"
													value={shippingInfo.shipping_address.street}
													onChange={(e) => handleAddressChange("street", e.target.value)}
													required
												/>
											</div>
											<div className="space-y-1.5">
												<Label htmlFor="interno">Scala / Interno</Label>
												<Input
													id="interno"
													placeholder="Facoltativo"
													value={shippingInfo.shipping_address.apartment}
													onChange={(e) => handleAddressChange("apartment", e.target.value)}
												/>
											</div>
										</div>
										<div className="grid grid-cols-1 md:grid-cols-3 gap-4">
											<div className="space-y-1.5">
												<Label htmlFor="cap">CAP *</Label>
												<Input
													id="cap"
													placeholder="31019"
													maxLength={5}
													value={shippingInfo.shipping_address.postalCode}
													onChange={(e) => handleAddressChange("postalCode", e.target.value)}
													required
												/>
											</div>
											<div className="space-y-1.5">
												<Label htmlFor="citta">Città *</Label>
												<Input
													id="citta"
													placeholder="Portobuffolè"
													value={shippingInfo.shipping_address.city}
													onChange={(e) => handleAddressChange("city", e.target.value)}
													required
												/>
											</div>
											<div className="space-y-1.5">
												<Label htmlFor="provincia">Provincia *</Label>
												<Select
													value={shippingInfo.shipping_address.province || undefined}
													onValueChange={(value) => handleAddressChange("province", value)}
												>
													<SelectTrigger id="provincia">
														<SelectValue placeholder="Seleziona" />
													</SelectTrigger>
													<SelectContent>
														{PROVINCES.map((p) => (
															<SelectItem key={p} value={p}>
																{p}
															</SelectItem>
														))}
													</SelectContent>
												</Select>
											</div>
										</div>
										<div className="space-y-1.5">
											<Label htmlFor="note">Note per il corriere</Label>
											<Textarea
												id="note"
												placeholder="Citofono, orari preferiti, punto di consegna..."
												value={shippingInfo.notes}
												onChange={(e) =>
													setShippingInfo((prev) => ({ ...prev, notes: e.target.value }))
												}
												rows={3}
											/>
										</div>
										<label className="flex items-center gap-2 text-sm cursor-pointer">
											<Checkbox
												checked={shippingInfo.gift_wrap}
												onCheckedChange={(checked) =>
													setShippingInfo((prev) => ({ ...prev, gift_wrap: checked === true }))
												}
											/>
											<Gift className="h-4 w-4 text-primary" />
											Hai bisogno di confezioni regalo? Incartiamo i vasetti in carta paglia.
										</label>
									</div>

									{/* Sezione 3: Metodo di spedizione */}
									<div className="space-y-4">
										<h2 className="font-display text-lg font-semibold flex items-center gap-2">
											<span className="h-6 w-6 rounded-full bg-primary/10 text-primary text-xs font-bold flex items-center justify-center">3</span>
											Metodo di Spedizione
										</h2>
										<div className="space-y-3">
											{activeShippingMethods.map((method) => {
												const selected = shippingInfo.shipping_method === method.value;
												const cost = computeShippingCost(method, subtotal, parcelGrams, shippingTiers);
												return (
													<label
														key={method.value}
														className={cn(
															"flex items-start gap-3 rounded-xl border p-4 cursor-pointer transition-colors",
															selected ? "border-primary bg-primary/5" : "border-border hover:border-primary/40",
														)}
													>
														<input
															type="radio"
															name="shipping_method"
															className="mt-1 accent-primary"
															checked={selected}
															onChange={() =>
																setShippingInfo((prev) => ({ ...prev, shipping_method: method.value }))
															}
														/>
														<div className="flex-1 min-w-0">
															<div className="flex items-center justify-between gap-2">
																<span className="font-medium flex items-center gap-2">
																	{method.value === "pickup" ? (
																		<HomeIcon className="h-4 w-4 text-primary" />
																	) : (
																		<Truck className="h-4 w-4 text-primary" />
																	)}
																	{method.label}
																	{method.value === "pickup" && (
																		<Badge variant="eco">Consigliato</Badge>
																	)}
																</span>
																<span className="font-semibold">
																	{cost === null ? "—" : cost === 0 ? "Gratis" : formatEuro(cost)}
																</span>
															</div>
															<p className="text-sm text-muted-foreground mt-1">{method.description}</p>
															{method.value === WEIGHT_BASED_METHOD && parcelGrams > 0 && (
																<p className="text-xs text-muted-foreground mt-1">
																	Peso stimato del pacco: {(parcelGrams / 1000).toLocaleString("it-IT", { maximumFractionDigits: 1 })} kg
																</p>
															)}
														</div>
													</label>
												);
											})}
										</div>
									</div>

									<div className="flex justify-between pt-4 border-t border-border">
										<Button variant="ghost" size="lg" shape="card" onClick={() => setCheckoutStep("cart")}>
											<ArrowLeft className="h-4 w-4" />
											Torna al Carrello
										</Button>
									</div>
								</div>
							)}
						</div>

						{/* Riepilogo ordine */}
						<div className="lg:col-span-1">
							{checkoutStep === "cart" ? (
								<OrderSummary
									ctaLabel="Procedi alla Spedizione"
									ctaIcon={Truck}
									onCta={() => setCheckoutStep("shipping")}
								/>
							) : (
								<>
									<OrderSummary
										ctaLabel="Procedi al Pagamento"
										ctaIcon={CreditCard}
										onCta={handleStripeCheckout}
									/>
									<p className="mt-3 text-xs text-muted-foreground text-center">
										Procedendo al pagamento accetti le{" "}
										<Link to="/condizioni-di-vendita" className="underline hover:text-primary">
											Condizioni di vendita
										</Link>{" "}
										e confermi di aver letto la{" "}
										<Link to="/privacy" className="underline hover:text-primary">
											Privacy Policy
										</Link>{" "}
										e le informazioni sul{" "}
										<Link to="/resi" className="underline hover:text-primary">
											diritto di recesso
										</Link>
										.
									</p>
								</>
							)}
						</div>
					</div>
				)}
			</div>
		</section>
	);
}
